import React, { useState } from 'react';
import {
  FormControl,
  Select, MenuItem, FormHelperText, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, IconButton } from '@material-ui/core';
import styled from 'styled-components';
import { FulfilmentTypeEnum } from '../../../../../../apis/clinics/types';
import { Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

interface FulfilmentTypeSelectProps {
  fulfilmentType: FulfilmentTypeEnum | null | undefined;
  onTypeUpdate: (type: FulfilmentTypeEnum) => Promise<void>;
  hasListedOrganisations: boolean;
  listedOrgMessage: string;
  networkSettingsMessage: string;
  withinListingNetworkMessage: string;
  isGlobalDefault: boolean;
}

const FulfilmentTypeSelect: React.FC<FulfilmentTypeSelectProps> = ({
  fulfilmentType,
  onTypeUpdate,
  hasListedOrganisations,
  listedOrgMessage,
  networkSettingsMessage,
  withinListingNetworkMessage,
  isGlobalDefault
}) => {
  
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<FulfilmentTypeEnum>(
    fulfilmentType === undefined || fulfilmentType === null ? FulfilmentTypeEnum.NONE : fulfilmentType
  );

  const handleTypeChange = (newType: FulfilmentTypeEnum) => {
    setSelectedType(newType);
    setConfirmationDialogOpen(true);
  };

  const handleConfirmTypeChange = async () => {
    await onTypeUpdate(selectedType);
    setConfirmationDialogOpen(false);
  };

  const handleCancelTypeChange = () => {
    setSelectedType(fulfilmentType === undefined || fulfilmentType === null ? FulfilmentTypeEnum.NONE : fulfilmentType);
    setConfirmationDialogOpen(false);
  };

  const getFulfilmentTypeLabel = (type: FulfilmentTypeEnum) => {
    switch (type) {
      case FulfilmentTypeEnum.MARKETPLACE:
        return 'Healthera Marketplace Fulfilment';
      case FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS:
        return 'Specific Organisations';
      case FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK:
        return 'Within Listing Organisation Network';
      case FulfilmentTypeEnum.NONE:
        return 'None';
      default:
        return '';
    }
  };

  return (
    <FulfilmentTypeSection>
      <FormControl variant="outlined" style={{ width: '50%', }}>
      <label>Fulfilment Type</label>
        <SelectContainer>
          <Select
            displayEmpty
            value={fulfilmentType === undefined || fulfilmentType === null ? FulfilmentTypeEnum.NONE : fulfilmentType}
            onChange={(e) => {
              const value = e.target.value as FulfilmentTypeEnum;
              handleTypeChange(value);
            }}
            disabled={isGlobalDefault}
            style={{ width: '100%' }}
            renderValue={(selected) => {
              return getFulfilmentTypeLabel(selected as FulfilmentTypeEnum);
            }}
          >
            <MenuItem value={FulfilmentTypeEnum.NONE}>
              None
            </MenuItem>
            <MenuItem value={FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS}>
              {getFulfilmentTypeLabel(FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS)}
            </MenuItem>
            <MenuItem value={FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK}>
              {getFulfilmentTypeLabel(FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK)}
            </MenuItem>
            <MenuItem value={FulfilmentTypeEnum.MARKETPLACE}>
              {getFulfilmentTypeLabel(FulfilmentTypeEnum.MARKETPLACE)}
            </MenuItem>
          </Select>
          <Tooltip
            title={
              <div>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}><strong>None:</strong></Typography>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}>
                  Remove all fulfilment options for this clinic. If you have Listed Organisations, fulfilment will go to these organisations. If a White Label is linked to this clinic, fulfilment will stay within the White Label.

                </Typography>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}><strong>Specific Organisations:</strong></Typography>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}>Select specific pharmacies and organisations below that can fulfill prescriptions for this clinic.</Typography>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}><strong>Within Listing Organisation Network:</strong></Typography>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}>Allow fulfillment through the network of pharmacies associated in the "Listing Organisations" tab.</Typography>
                <Typography variant="body2" style={{ marginBottom: 8, color: 'white' }}><strong>Healthera Marketplace Fulfilment:</strong></Typography>
                <Typography variant="body2" style={{ color: 'white' }}>Allow fulfillment through the Healthera network of pharmacies.</Typography>
              </div>
            }
            placement="right"
          >
            <IconButton style={{ padding: 4, marginLeft: 8 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </SelectContainer>
        {selectedType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK && !hasListedOrganisations && (
          <FormHelperText error>
            Please add at least one listing organisation in the Listing Organisations tab before selecting this option.
          </FormHelperText>
        )}
        {selectedType && selectedType !== FulfilmentTypeEnum.NONE && selectedType === fulfilmentType && (
          <FormHelperText>
            {selectedType === FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS
              ? listedOrgMessage
              : selectedType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK
                ? withinListingNetworkMessage
                : networkSettingsMessage}
          </FormHelperText>
        )}
      </FormControl>

      <Dialog open={confirmationDialogOpen} onClose={handleCancelTypeChange}>
        <DialogTitle>
          {selectedType !== FulfilmentTypeEnum.NONE ? 'Change Fulfilment Type' : 'Remove Fulfilment Type'}
        </DialogTitle>
        <DialogContent>
          {selectedType !== FulfilmentTypeEnum.NONE ? (
            <>
              Are you sure you want to change the fulfilment type to {getFulfilmentTypeLabel(selectedType)}?
              {selectedType === FulfilmentTypeEnum.MARKETPLACE && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  This clinic's orders will be fulfilled by the Healthera network of pharmacies.
                </Typography>
              )}
              {selectedType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK && !hasListedOrganisations && (
                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                  Please add at least one listing organisation in the Listing Organisations tab before selecting this option.
                </Typography>
              )}
            </>
          ) : (
            <>
              Are you sure you want to remove the fulfilment type?
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              If you have Listed Organisations, fulfilment will go to these organisations.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTypeChange} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmTypeChange}
            color="primary"
            variant="contained"
            disabled={selectedType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK && !hasListedOrganisations}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </FulfilmentTypeSection>
  );
};

const FulfilmentTypeSection = styled.div`
  margin-bottom: 24px;
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export default FulfilmentTypeSelect;
